/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/default-props-match-prop-types */
/* eslint-disable react/sort-comp, react/require-default-props */
import React from 'react';
import _ from 'underscore';

import type { I18nLike } from '@bingads-webui/common-types';
import { MessageBar } from '@bingads-webui-react/fabric-control';
import { Pagination } from '@bingads-webui-react/pagination';
import { SearchBoxWithLiveResults, getSearchResultLiveRegionText } from '@bingads-webui-react/search-box';
import { ListSelector } from '@bingads-webui-react/list-selector';
import type {
  IRawListItemItem,
  IRawListSelectorProps,
} from '@bingads-webui-react/list-selector';

export interface IEntitySelectorI18n extends Pick<I18nLike, 'getString'> {
  parseDecimal: (value: string) => number | null;
}

export interface IEntitySelectorFetchOptions {
  query: string;
  pageNumber: number;
}

export interface IEntitySelectorMessageConfig {
  text?: React.ReactNode;
}

type EntitySelectorListProps<TItem extends object> = Pick<
  IRawListSelectorProps<TItem>,
  | 'checkSelected'
  | 'onSelect'
  | 'renderActionBar'
  | 'renderEmptyList'
  | 'renderFooter'
  | 'renderHeader'
  | 'renderItem'
>;

export type IEntitySelectorProps<TItem extends object = IRawListItemItem> =
  EntitySelectorListProps<TItem> & {
    // Config props
    i18n: IEntitySelectorI18n;
    pageSize: number;
    previousComponent: HTMLElement | null;
    ariaLabel: string;
    searchBoxAriaLabel: string;
    searchBoxPlaceholder: string;
    messageConfig: IEntitySelectorMessageConfig;
    // Data props
    items: TItem[];
    pageNumber?: number;
    totalCount: number;
    selectedItem: TItem | null;
    // Flag props
    isShowSearchbox: boolean;
    enableRandomPage: boolean;
    enableArrowKeysSupport: boolean;
    enableOverflowTooltip: boolean;
    onFetch: (options: IEntitySelectorFetchOptions) => void;
  };

interface IEntitySelectorState {
  pageNumber: number;
  query: string;
}

export class EntitySelector<TItem extends object = IRawListItemItem>
  extends React.Component<IEntitySelectorProps<TItem>, IEntitySelectorState> {
  static defaultProps: Partial<IEntitySelectorProps<IRawListItemItem>> = {
    pageSize: 10,
    selectedItem: null,
    renderHeader: undefined,
    renderEmptyList: undefined,
    renderActionBar: undefined,
    renderFooter: undefined,
    isShowSearchbox: true,
    enableRandomPage: false,
    enableArrowKeysSupport: false,
    ariaLabel: '',
    searchBoxAriaLabel: '',
    searchBoxPlaceholder: '',
    checkSelected: (selectedItem, item) => _.isEqual(selectedItem, item),
    enableOverflowTooltip: false,
    messageConfig: {},
    previousComponent: null,
  };

  paginationRef: React.RefObject<HTMLDivElement>;

  searchBoxRef: React.RefObject<HTMLDivElement>;

  static getDerivedStateFromProps(
    nextProps: IEntitySelectorProps,
    prevState: IEntitySelectorState
  ): Partial<IEntitySelectorState> | null {
    const { pageNumber } = nextProps;
    if (pageNumber !== undefined && pageNumber !== prevState.pageNumber) {
      return {
        pageNumber,
      };
    }
    return null;
  }

  state = {
    pageNumber: 0,
    query: '',
  };

  constructor(props: IEntitySelectorProps<TItem>) {
    super(props);
    this.paginationRef = React.createRef();
    this.searchBoxRef = React.createRef();
  }

  onPageNumberChanged = (pageNumber: number) => {
    this.onFetch({ query: this.state.query, pageNumber });
  };

  onQueryChange = (query: string) => {
    this.setState({ query });
  };

  onSearch = (query: string) => {
    this.onFetch({ query, pageNumber: 0 });
  };

  onFetch = ({ query, pageNumber }: IEntitySelectorFetchOptions) => {
    this.setState({
      pageNumber,
      query,
    });

    this.props.onFetch({ query, pageNumber });
  };

  onListSelectorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        // Handle Shift+Tab key press
        if (this.props.previousComponent && this.props.previousComponent.focus) {
          event.preventDefault(); // Prevent the default tab behavior
          event.stopPropagation(); // Prevent the event from bubbling up
          this.props.previousComponent.focus();
        } else if (this.props.isShowSearchbox && this.searchBoxRef.current) {
          const searchIcon = this.searchBoxRef.current.querySelector<HTMLElement>('.search-box__icon');
          if (searchIcon && searchIcon.focus) {
            searchIcon.focus();
          }
        }
      } else if (this.paginationRef.current) {
        const prevButton = this.paginationRef.current.querySelector<HTMLElement>('.btn.btn-default.prev');
        const nextButton = this.paginationRef.current.querySelector<HTMLElement>('.btn.btn-default.next');
        event.preventDefault(); // Prevent the default tab behavior
        event.stopPropagation(); // Prevent the event from bubbling up
        if (this.paginationRef.current.querySelector('.btn.btn-default.prev[disabled]')) {
          if (nextButton && nextButton.focus) {
            nextButton.focus();
          }
        } else if (prevButton && prevButton.focus) {
          prevButton.focus();
        }
      }
    }
  };

  renderSearchbox = () => {
    const {
      i18n, isShowSearchbox, totalCount, searchBoxPlaceholder,
    } = this.props;
    if (isShowSearchbox) {
      return (
        <div className="search-box-container" ref={this.searchBoxRef}>
          <SearchBoxWithLiveResults
            ariaLabel={this.props.searchBoxAriaLabel}
            onQueryChange={this.onQueryChange}
            onSearch={this.onSearch}
            value={this.state.query}
            placeholder={searchBoxPlaceholder || i18n.getString('EntitySelector_Search_Placeholder')}
            screenReaderText={getSearchResultLiveRegionText(
              totalCount,
              i18n.getString('No results found for {{searchText}}', { searchText: this.state.query }),
              i18n.getString('Found {{totalCount}} Results for {{searchText}}', { totalCount, searchText: this.state.query })
            )}
            aria-live="assertive"
          />
        </div>
      );
    }
    return null;
  };

  render() {
    const { i18n, messageConfig } = this.props;
    return (
      <div className="ba-entity-selector">
        {this.renderSearchbox()}

        <div className="list-selector-container">
          {
            !_.isEmpty(this.props.messageConfig) &&
            <MessageBar className="list_selector_message_bar">
              {messageConfig.text}

            </MessageBar>
          }
          <ListSelector
            renderHeader={this.props.renderHeader}
            renderItem={this.props.renderItem}
            renderActionBar={this.props.renderActionBar}
            renderFooter={this.props.renderFooter}
            onSelect={this.props.onSelect}
            data={this.props.items}
            onKeyDown={this.onListSelectorKeyDown}
            renderEmptyList={this.props.renderEmptyList}
            selectedItem={this.props.selectedItem}
            checkSelected={this.props.checkSelected}
            enableArrowKeysSupport={this.props.enableArrowKeysSupport}
            ariaLabel={this.props.ariaLabel}
            enableOverflowTooltip={this.props.enableOverflowTooltip}
          />
        </div>

        {
          this.props.totalCount > this.props.pageSize &&
          <div className="pagination-container" ref={this.paginationRef}>
            <Pagination
              onPageNumberChanged={this.onPageNumberChanged}
              pageNumber={this.state.pageNumber}
              itemCount={this.props.totalCount}
              pageSize={this.props.pageSize}
              pageText={i18n.getString('EntitySelector_Pagination_Page')}
              totalPageCountText={i18n.getString('EntitySelector_Pagination_TotalPageCountText')}
              previousPageText={i18n.getString('EntitySelector_Pagination_PreviousPageText')}
              nextPageText={i18n.getString('EntitySelector_Pagination_NextPageText')}
              i18n={i18n}
              enableRandomPage={this.props.enableRandomPage}
            />
          </div>
        }
      </div>
    );
  }
}
